import { Box, IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { InvoiceRowActions } from "../components/InvoiceRowActions"
import { InvoicesDatePaidCell } from "../components/InvoicesDatePaidCell"
import { InvoiceStatusCell } from "../components/InvoiceStatusCell"
import { INVOICE_PAYMENT_METHOD_LABELS, INVOICE_TYPE_LABELS } from "../constants/labels"
import { InvoicesSortableAccessorKey } from "../constants/sorting"
import { formatInvoiceCurrency } from "../utils"

import StyledChip from "@/components/common/Chip/Chip"
import type { Invoice, InvoiceAs400SyncKind } from "@/graphql/codegen/graphql"
import { formatDate } from "@/utils/date"

interface UseInvoicesColumnsProps {
  onEdit?: (row: Invoice) => void
  onView?: (row: Invoice) => void
  onCancel?: (row: Invoice) => void
  onVoid?: (row: Invoice) => void
  onConfirmAs400Reconciliation?: (row: Invoice, syncKind: InvoiceAs400SyncKind) => void
}

export const useInvoicesColumns = ({
  onEdit,
  onView,
  onCancel,
  onVoid,
  onConfirmAs400Reconciliation,
}: UseInvoicesColumnsProps = {}): ColumnDef<Invoice>[] => {
  return useMemo<ColumnDef<Invoice>[]>(
    () => [
      {
        accessorKey: InvoicesSortableAccessorKey.VendorInvoiceNumber,
        header: "Vendor Invoice #",
        cell: ({ row }) => row.original.invoiceNumber,
        enableSorting: true,
        size: 140,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.Vendor,
        header: "Vendor",
        cell: ({ row }) => row.original.vendorCompany?.name ?? "",
        enableSorting: true,
        size: 180,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.InvoiceType,
        header: "Type",
        cell: ({ row }) => (
          <StyledChip
            label={INVOICE_TYPE_LABELS[row.original.invoiceType]}
            className='bg-primary-200 text-gray-900'
            size='small'
          />
        ),
        enableSorting: true,
        size: 90,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.DateEntered,
        header: "Date Entered",
        cell: ({ row }) => (row.original.createdOn ? formatDate(row.original.createdOn) : ""),
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.DueDate,
        header: "Due Date",
        cell: ({ row }) => formatDate(row.original.dueDate),
        enableSorting: true,
        size: 110,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.ScheduledPayDate,
        header: "Scheduled Pay Date",
        cell: ({ row }) => formatDate(row.original.scheduledPayDate),
        enableSorting: true,
        size: 150,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.PaymentMethod,
        header: "Payment Type",
        cell: ({ row }) => INVOICE_PAYMENT_METHOD_LABELS[row.original.paymentMethod],
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.Amount,
        header: "Amount",
        cell: ({ row }) => {
          const total = row.original.expenseLines.reduce((sum, line) => sum + line.amount, 0)
          return <span className='tabular-nums'>{formatInvoiceCurrency(total)}</span>
        },
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.PrintedCheck,
        header: "Printed Check",
        cell: ({ row }) => (row.original.printedCheck ? "Yes" : "No"),
        enableSorting: true,
        size: 110,
      },
      {
        accessorKey: InvoicesSortableAccessorKey.Status,
        header: "Status",
        cell: ({ row }) => <InvoiceStatusCell invoice={row.original} />,
        enableSorting: true,
        size: 130,
      },
      {
        // No server sort field for the derived paidDate value.
        id: "datePaid",
        header: "Date Paid",
        cell: ({ row }) => <InvoicesDatePaidCell invoice={row.original} />,
        enableSorting: false,
        size: 110,
      },
      {
        // Combined actions + expander column per tables.md "Combined Actions and Expander Column".
        id: "actions",
        header: "",
        size: 112,
        enableSorting: false,
        cell: ({ row }) => (
          <Box className='flex items-center justify-end gap-x-3'>
            <InvoiceRowActions
              row={row}
              onEdit={onEdit}
              onView={onView}
              onCancel={onCancel}
              onVoid={onVoid}
              onConfirmAs400Reconciliation={onConfirmAs400Reconciliation}
            />
            <IconButton
              size='small'
              onClick={row.getToggleExpandedHandler()}
              data-testid={`invoices.row-actions.expand-button-${row.original.invoiceId}`}
            >
              {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        ),
      },
    ],
    [onEdit, onView, onCancel, onVoid, onConfirmAs400Reconciliation]
  )
}
